import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useGetJobPositionByIdQuery } from 'store/api/jobPositions';
import { JobPositionForm } from 'types/jobPositions';
import { useClassifiers } from 'utils/useClassifiers';
import { JOB_POSITION_FORM_CLASSIFIERS } from './form-sections/useJobPositionForm';
import { jobPositionDtoToForm } from './jobPositionFormMapping';
import JobPositionPageForm from './JobPositionPageForm';

const ClassicJobPositionPageEdit = () => {
    const { id: jobPositionId } = useParams();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: JOB_POSITION_FORM_CLASSIFIERS,
    });

    const { data: jobPosition, isLoading, isError, refetch } = useGetJobPositionByIdQuery(jobPositionId ?? skipToken);

    const initialData = useMemo<JobPositionForm | undefined>(() => {
        if (!jobPosition) {
            return undefined;
        }

        return jobPositionDtoToForm(jobPosition, classifiers);
    }, [jobPosition, classifiers]);

    if (isLoading || isLoadingClassifiers || (!initialData && !isError)) {
        return <Loader />;
    }

    return (
        <JobPositionPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={jobPosition?.customFields}
        />
    );
};

/**
 * In the new design a job position is edited card by card on its page, so its edit URL opens the first card there,
 * on the path it came through (the Admin area's or the plain one).
 */
const JobPositionPageEdit = () => {
    const isNew = useIsNewDesign();
    const { pathname } = useLocation();
    return isNew ? (
        <Navigate to={`${pathname.replace(/\/edit\/?$/, '')}?edit=job-position`} replace />
    ) : (
        <ClassicJobPositionPageEdit />
    );
};

export default JobPositionPageEdit;
